// src/features/scheduledMessages/ScheduledMessages.tsx

import React, { useContext, useEffect, useState } from "react";
import dayjs from "dayjs";
import {
  Box,
  Button,
  TextField,
  Paper,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Typography,
} from "@mui/material";

import {
  CreateScheduledMessage,
  MessageStatus,
  ScheduledMessage,
  TemplateKey,
} from "../../models/scheduledMessages.model";

import {
  createScheduledMessage,
  deleteScheduledMessage,
  getScheduledMessages,
  updateScheduledMessageStatus,
} from "../../services/scheduledMessages.service";

import BusinessSettingsContext from "../Context/Settings/BusinessSettingsContext";

/* ---------------- TEMPLATE OPTIONS ---------------- */

const TEMPLATE_OPTIONS: { key: TemplateKey; label: string }[] = [
  { key: "FEEDBACK_V1", label: "Feedback Message" },
  { key: "ORDER_REMINDER_V1", label: "Order Reminder" },
];

/* ---------------- COMPONENT ---------------- */

const ScheduledMessages: React.FC = () => {
  const { settings } = useContext(BusinessSettingsContext);

  const businessId = settings?.businessId;

  const [rows, setRows] = useState<ScheduledMessage[]>([]);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [templateKey, setTemplateKey] = useState<TemplateKey>("FEEDBACK_V1");
  const [loading, setLoading] = useState(false);

  /* -------- LOAD -------- */

  const loadMessages = async () => {
    if (!businessId) return;

    try {
      const data = await getScheduledMessages(businessId);
      setRows(data);
    } catch (err) {
      console.error("Failed to load scheduled messages", err);
    }
  };

  useEffect(() => {
    loadMessages();
  }, [businessId]);

  /* -------- CREATE -------- */

  const handleCreate = async () => {
    if (!name || !phone || !businessId) return;

    const payload: CreateScheduledMessage = {
      businessId,
      templateKey,
      type: templateKey === "FEEDBACK_V1" ? "feedback" : "order_reminder",
      customer: { name, phone },
      payload: { businessName: settings?.name || "" },
      sendAt: new Date().toISOString(),
    };

    try {
      setLoading(true);
      await createScheduledMessage(payload);
      setName("");
      setPhone("");
      await loadMessages();
    } catch (err) {
      console.error("Failed to create scheduled message", err);
    } finally {
      setLoading(false);
    }
  };

  /* -------- ACTIONS -------- */

  const cancelMessage = async (id: string) => {
    try {
      await updateScheduledMessageStatus(id, "cancelled");
      await loadMessages();
    } catch (err) {
      console.error("Failed to cancel message", err);
    }
  };

  const deleteMessage = async (id: string) => {
    try {
      await deleteScheduledMessage(id);
      await loadMessages();
    } catch (err) {
      console.error("Failed to delete message", err);
    }
  };

  /* -------- STATUS CHIP -------- */

  const statusColor = (status: MessageStatus) => {
    switch (status) {
      case "pending":
        return "warning";
      case "sent":
        return "success";
      case "failed":
        return "error";
      case "processing":
        return "info";
      default:
        return "default";
    }
  };

  /* ---------------- UI ---------------- */

  if (!businessId) {
    return (
      <Box p={4}>
        <Typography color="text.secondary">
          Please complete Business Settings to enable Scheduled Messages.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, minWidth: 0 }}>
      {/* -------- CREATE FORM -------- */}
      <Paper variant="outlined" sx={{ p: 2.5, mb: 3, display: "flex", gap: 2, flexWrap: "wrap", alignItems: "flex-end" }}>
        <TextField
          label="Customer Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          size="small"
        />

        <TextField
          label="Phone"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          size="small"
        />

        <FormControl sx={{ minWidth: 200 }} size="small">
          <InputLabel>Template</InputLabel>
          <Select
            value={templateKey}
            label="Template"
            onChange={(e) => setTemplateKey(e.target.value as TemplateKey)}
          >
            {TEMPLATE_OPTIONS.map((t) => (
              <MenuItem key={t.key} value={t.key}>
                {t.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Button variant="contained" onClick={handleCreate} disabled={loading}>
          Schedule
        </Button>
      </Paper>

      {/* -------- TABLE -------- */}
      <TableContainer>
        <Table size="small" aria-label="Scheduled messages" sx={{ minWidth: 760 }}>
          <TableHead>
            <TableRow>
              <TableCell>Customer</TableCell>
              <TableCell>Phone</TableCell>
              <TableCell>Template</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Send At</TableCell>
              <TableCell>Attempts</TableCell>
              <TableCell>Action</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {rows.map((r) => (
              <TableRow key={r._id}>
                <TableCell>{r.customer?.name}</TableCell>
                <TableCell>{r.customer?.phone}</TableCell>
                <TableCell>{r.templateKey}</TableCell>

                <TableCell>
                  <Chip
                    size="small"
                    label={r.status}
                    color={statusColor(r.status)}
                  />
                </TableCell>

                <TableCell>
                  {dayjs(r.sendAt).format("DD MMM YYYY, hh:mm A")}
                </TableCell>

                <TableCell>{r.attempts}</TableCell>

                <TableCell>
                  {r.status === "pending" && (
                    <Button
                      size="small"
                      color="warning"
                      onClick={() => cancelMessage(r._id)}
                    >
                      Cancel
                    </Button>
                  )}
                  <Button
                    size="small"
                    color="error"
                    onClick={() => deleteMessage(r._id)}
                  >
                    Delete
                  </Button>
                </TableCell>
              </TableRow>
            ))}

            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  No scheduled messages
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default ScheduledMessages;
